const article = {
	slug: "wiser-bite-same-food-different-wisdom",
	title: "Same Food, Different Wisdom",
	date: "14 July 2026",
	updated: null,
	description:
		"Most food apps give a food one fixed score. Wiser Bite treats food as contextual, keeps the facts deterministic so the model never invents one, and then extends the same idea to movement and intrusive thoughts.",
	keywords: [
		"RAG",
		"local-first AI",
		"Transformers.js",
		"health tech",
		"product design",
	],
	tldr: "A mango is not one number for everyone. Wiser Bite answers wise for whom, when, and at what pace, with the facts held deterministic and only the voice generated, and the same contextual idea now covers movement and thought loops too.",
	readingTime: null,
	provenance: null,
	provenanceUrl: null,
	series: null,
	artifact: null,
	hero: {
		src: "/projects/wiser-bite.png",
		alt: "A Wiser Bite Wisdom Card for an Indian food",
		caption: "Same food, different wisdom.",
	},
	markdown: `Every food app I have used gives a food one score and stops there. A mango is "high sugar." A banana is "good potassium." The number is fixed, and it is the same number for a pregnant woman, a marathoner, and a sixty-year-old managing blood sugar.

That is not how food actually works, and after two years inside a structured diabetes-reversal program I could not unsee it. A mango at breakfast after a workout is a different decision than the same mango late on a monsoon evening in the strict early weeks of a reset. Same food. Different wisdom.

## The question I wanted to answer

"Is this healthy?" is the wrong question. The one worth answering is "wise for whom, when, in what phase, and at what pace of change?"

So Wiser Bite starts there. You pick an Indian food, tap a chip or two for context (who you are, how you honestly feel right now, the time and season, where you are in a health journey), and you get a Wisdom Card: a contextual verdict, plain reasoning, one concrete swap, and a safety line. No account. No food logging. No dashboard that finally pays off after three weeks of work.

## Why I made the model never invent a fact

Health is the one place I did not want a chatbot improvising. So the system splits the work. The facts and the safety are deterministic, and only the voice is generated.

A knowledge base of Indian-first foods holds curated attributes like a glycemic band and whether a food is whole or refined. A rules engine turns those into a verdict of great, okay, or caution using human-readable modifiers that a domain expert can read and amend without touching code. Only after the verdict is fixed does a small model phrase it warmly. If any step fails, a static composer writes the card instead, and the safety footer is a code constant no model can rewrite.

The retrieval half runs entirely in your browser. A small embedding model loads after first paint and searches a short corpus of principles on device, so nothing about your profile leaves your phone.

## The part I did not plan

I built the emotional axis because it is the real reason people eat against their own knowledge. "Craving comfort." "About to self-sabotage." "Celebrating." Those change how the card is phrased, mostly without changing the verdict, because judgment is what makes people close a health app.

Once that was working, I noticed the same shape applied outside the plate.

The bad food decision, the skipped walk, and the spiral of intrusive thoughts are the same moment wearing different clothes. You know the good move, and right then the good move feels impossibly far away. What helps is not a lecture. It is one smaller, kinder option you can actually take now.

So I added two sibling tabs that reuse the same engine.

**Move** hands you a tiny piece of movement matched to how you feel, the kind with almost no friction to start. Not a workout plan. One thing.

**Mind** does for a thought loop what the food tab does for a craving. When the intrusive thought shows up, it offers a better option than arguing with it, in the same plain and non-judgmental voice.

None of this is medical advice, and the app says so on every card. It is general education, and keeping it contextual is exactly what keeps it there.

## What it taught me

I set out to build a smarter food lookup. What I actually built was a small argument that most "good for you" advice fails at the only moment that counts, the moment you decide, because it ignores context and leads with judgment.

Fix those two things and the idea travels. From the plate, to the body, to the mind.

Live at [wiserbite.swapniltamse.com](https://wiserbite.swapniltamse.com).`,
};

export default article;
